import React from "react";
//#region types
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { RutRotSelectionProps, RutRotSummaryProps } from "models/propTypes/invoicePropTypes";
import { TranslationKeys } from "translations";
import { CommonConstants } from "utils/constants";
import { InvoiceFormTypes } from "models/formTypes";
import { InputProps, NumberInputProps, SelectionChipsProps, TextInputProps } from "models/propTypes/wintFormPropsTypes";
import { WintFormKey } from "models/propTypes/wintFormPropsTypes";
//#endregion
//#region components
import {
  CurrencyInput,
  HorizontalFormField,
  NumberInput,
  TextInput,
  WizardSection,
  SelectionChips,
} from "components/common/wintForms";
import RutRotSummary from "./rutRotSummary";
import RutRotSelector from "./rutRotSelector";
import { useInvoiceService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

const RutRotSelectionStep: React.FC<RutRotSelectionProps> = (props) => {
  const invoiceService = useInvoiceService();
  const { connect, values, errors, touched, submitted, wizardProps: wizardStepProps, hasROT, hasRUT } = props;

  const isRotProperty = (values: InvoiceFormTypes.Create, type: WintSuperkollAPIModel.InvoiceRotPropertyType) => {
    return values.rutRot?.houseWorkType && values.rutRot?.rotPropertyType
      ? values.rutRot.houseWorkType === WintSuperkollAPIModel.InvoiceHouseWorkType.ROT &&
          type === values.rutRot.rotPropertyType
      : false;
  };

  const hasErrors = connect("rutRot").hasErrors;

  const onProductsUpdate = () => {
    const products: InvoiceFormTypes.Product[] = values.products ?? [];

    if (values.rutRot?.rutRotArticles && values.rutRot.rutRotArticles.length > 0) {
      // Remove rut rot articles that are not in the product list
      if (values.rutRot.rutRotArticles.some((rr) => !products.some((p) => p.uniqueId === rr.id))) {
        const filteredRutRotArticles = values.rutRot.rutRotArticles.filter((rr) =>
          products.some((p) => p.uniqueId === rr.id)
        );

        const rutRotArticlesFieldName: WintFormKey<InvoiceFormTypes.Create> = "rutRot.rutRotArticles";
        const rutRotOtherCostsFieldName: WintFormKey<InvoiceFormTypes.Create> = "rutRot.houseWorkOtherCosts";
        const otherCost = invoiceService.calculateRutRotOtherCost(products, filteredRutRotArticles);
        connect(rutRotArticlesFieldName).setValue(filteredRutRotArticles);
        connect(rutRotOtherCostsFieldName).setValue(otherCost);
      }
    }
  };

  React.useEffect(() => {
    onProductsUpdate();
  }, [values.products]);

  return (
    <WizardSection
      header={invoiceService.getRutRotTabHeader(hasROT, hasRUT)}
      description={TranslationKeys.Invoice.RutRotDescription}
      submitted={submitted}
      wizardProps={wizardStepProps}
      hasErrors={hasErrors}
      testId="rutRotSelectionStep"
    >
      <HorizontalFormField<SelectionChipsProps>
        Component={SelectionChips}
        {...connect("rutRot.houseWorkType")}
        label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.RutRotInvoice)}
        descriptionText={LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.Invoice.RutRotInvoiceDescription
        )}
        items={invoiceService.getHouseWorkTypes()}
        disabledItems={invoiceService.getDisabledHouseWorkTypes(hasROT, hasRUT)}
      />
      {values.rutRot?.houseWorkType ? (
        <HorizontalFormField<NumberInputProps>
          Component={NumberInput}
          {...connect("rutRot.rutRotPersonNumber")}
          label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.CustomerPersonNumber)}
          descriptionText={LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Invoice.RutRotPersonNumberDescription,
            "",
            { houseWorkType: invoiceService.getRutOrRotText(values.rutRot?.houseWorkType) }
          )}
          allowLeadingZeros
          format={CommonConstants.PersonNumberLongMask}
        />
      ) : (
        <></>
      )}
      {values.rutRot?.houseWorkType === WintSuperkollAPIModel.InvoiceHouseWorkType.ROT ? (
        <HorizontalFormField<SelectionChipsProps>
          Component={SelectionChips}
          {...connect("rutRot.rotPropertyType")}
          label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.RotPropertyType)}
          descriptionText={LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Invoice.RotPropertyTypeDescription
          )}
          items={invoiceService.getRotPropertyTypes()}
        />
      ) : (
        <></>
      )}
      {isRotProperty(values, WintSuperkollAPIModel.InvoiceRotPropertyType.House) ? (
        <HorizontalFormField<TextInputProps>
          Component={TextInput}
          {...connect("rutRot.propertyDesignation")}
          label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.PropertyDesignation)}
          descriptionText={LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Invoice.PropertyDesignationDescription
          )}
          maxLength={40}
        />
      ) : isRotProperty(values, WintSuperkollAPIModel.InvoiceRotPropertyType.Condominium) ? (
        <>
          <HorizontalFormField<NumberInputProps>
            Component={NumberInput}
            {...connect("rutRot.tenantsOrgNumber")}
            label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.TenantsOrgNumber)}
            allowLeadingZeros
            format={CommonConstants.OrganizationNumberMask}
          />
          <HorizontalFormField<TextInputProps>
            Component={TextInput}
            {...connect("rutRot.apartmentNumber")}
            label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.ApartmentNumber)}
          />
        </>
      ) : (
        <></>
      )}
      {values.rutRot?.houseWorkType ? (
        <>
          <RutRotSelector connect={connect} values={values} errors={errors} touched={touched} />
          <HorizontalFormField<InputProps>
            Component={CurrencyInput}
            {...connect("rutRot.houseWorkOtherCosts")}
            label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.HouseWorkOtherCosts)}
            descriptionText={LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.Invoice.HouseWorkOtherCostsDescription
            )}
          />
          <HorizontalFormField<RutRotSummaryProps>
            Component={RutRotSummary}
            {...connect("rutRot.rutRotSummary")}
            label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.RutRotSummary)}
            descriptionText={LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.Invoice.RutRotSummaryDescription
            )}
            values={values}
          />
        </>
      ) : (
        <></>
      )}
    </WizardSection>
  );
};

export default RutRotSelectionStep;
